import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link } from 'react-router-dom';
import { WebSocketProvider } from './contexts/WebSocketContext';
import LayoutRouter from './components/layouts/LayoutRouter';
import { LiveMatchViewRouter } from './components/LiveMatchViewRouter';
import { LineupManager } from './components/LineupManager';
import { LoginPage } from './components/LoginPage';
import MatchManager from './components/MatchManager';
import Dashboard from './pages/Dashboard';
import Teams from './pages/Teams';
import Players from './pages/Players';
import EventTypes from './pages/EventTypes';
import Config from './pages/Config';
import Settings from './pages/Settings';
import MatchDetail from './pages/MatchDetail';
import TestingTools from './pages/TestingTools';
import PNGTransparencyTest from './pages/PNGTransparencyTest';
import { ScoreDisplayTest } from './components/ScoreDisplayTest';
import { MatchTimeControlsTest } from './components/MatchTimeControlsTest';
import { MatchStatusControlTest } from './components/MatchStatusControlTest';
import { FontTestPage } from './components/FontTestPage';
import ButtonTestPage from './components/ButtonTestPage';
import BroadcastMinTestPage from './components/BroadcastMinTestPage';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsAuthenticated(!!token);
    setLoading(false);
  }, []);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // Dispatch custom event for WebSocket cleanup
    window.dispatchEvent(new CustomEvent('tokenChanged', { 
      detail: { action: 'logout' }
    }));
    
    setIsAuthenticated(false);
    window.location.href = '/login';
  };

  if (loading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Public route - uses LayoutRouter with PublicWebSocketProvider */}
        <Route path="/watch/:matchId" element={<LayoutRouter />} />
        
        {/* All other routes wrapped in WebSocketProvider */}
        <Route path="/*" element={
          <WebSocketProvider>
            <Routes>
              {/* Login route */}
              <Route path="/login" element={
                isAuthenticated 
                  ? <Navigate to="/dashboard" replace /> 
                  : <LoginPage onLoginSuccess={handleLoginSuccess} />
              } />
              
              {/* Authenticated routes */}
              <Route path="/*" element={
                isAuthenticated 
                  ? <AuthenticatedApp onLogout={handleLogout} /> 
                  : <Navigate to="/login" replace />
              } />
            </Routes>
          </WebSocketProvider>
        } />
      </Routes>
    </BrowserRouter>
  );
}

// Protected app with navigation
function AuthenticatedApp({ onLogout }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow-md">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 focus:outline-none"
              aria-label="Toggle menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {mobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>

            {/* Desktop Navigation Links */}
            <div className="hidden md:flex space-x-6">
              <Link to="/dashboard" className="text-gray-700 hover:text-blue-600 font-medium">
                Dashboard
              </Link>
              <Link to="/matches" className="text-gray-700 hover:text-blue-600 font-medium">
                Matches
              </Link>
              <Link to="/teams" className="text-gray-700 hover:text-blue-600 font-medium">
                Teams
              </Link>
              <Link to="/players" className="text-gray-700 hover:text-blue-600 font-medium">
                Players
              </Link>
              <Link to="/event-types" className="text-gray-700 hover:text-blue-600 font-medium">
                Event Types
              </Link>
              <Link to="/config" className="text-gray-700 hover:text-blue-600 font-medium">
                Config
              </Link>
              <Link to="/settings" className="text-gray-700 hover:text-blue-600 font-medium">
                Broadcast
              </Link>
              <Link to="/testing" className="text-gray-700 hover:text-blue-600 font-medium">
                🧪 Testing
              </Link>
            </div>
            <button
              onClick={onLogout}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Logout
            </button>
          </div>

          {/* Mobile Navigation Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pt-4 border-t border-gray-200">
              <div className="flex flex-col space-y-3">
                <Link
                  to="/dashboard"
                  className="text-gray-700 hover:text-blue-600 font-medium py-2 px-3 rounded hover:bg-gray-100"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
                <Link
                  to="/matches"
                  className="text-gray-700 hover:text-blue-600 font-medium py-2 px-3 rounded hover:bg-gray-100"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Matches
                </Link>
                <Link
                  to="/teams"
                  className="text-gray-700 hover:text-blue-600 font-medium py-2 px-3 rounded hover:bg-gray-100"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Teams
                </Link>
                <Link
                  to="/players"
                  className="text-gray-700 hover:text-blue-600 font-medium py-2 px-3 rounded hover:bg-gray-100"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Players
                </Link>
                <Link
                  to="/event-types"
                  className="text-gray-700 hover:text-blue-600 font-medium py-2 px-3 rounded hover:bg-gray-100"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Event Types
                </Link>
                <Link
                  to="/config"
                  className="text-gray-700 hover:text-blue-600 font-medium py-2 px-3 rounded hover:bg-gray-100"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Config
                </Link>
                <Link
                  to="/settings"
                  className="text-gray-700 hover:text-blue-600 font-medium py-2 px-3 rounded hover:bg-gray-100"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Broadcast
                </Link>
                <Link
                  to="/testing"
                  className="text-gray-700 hover:text-blue-600 font-medium py-2 px-3 rounded hover:bg-gray-100"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  🧪 Testing
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/matches" element={<MatchManager />} />
        <Route path="/matches/:id" element={<MatchDetail />} />
        <Route path="/teams" element={<Teams />} />
        <Route path="/players" element={<Players />} />
        <Route path="/event-types" element={<EventTypes />} />
        <Route path="/config" element={<Config />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/testing" element={<TestingTools />} />
        <Route path="/test/score" element={<ScoreDisplayTest />} />
        <Route path="/test/clock" element={<MatchTimeControlsTest />} />
        <Route path="/test/status" element={<MatchStatusControlTest />} />
        <Route path="/test/png" element={<PNGTransparencyTest />} />
        <Route path="/test/fonts" element={<FontTestPage />} />
        <Route path="/test/buttons" element={<ButtonTestPage />} />
        <Route path="/test/broadcast-min" element={<BroadcastMinTestPage />} />
        <Route path="/live/:matchId" element={<LiveMatchViewWrapper />} />
        <Route path="/lineups/:matchId" element={<LineupManagerWrapper />} />
      </Routes>
    </div>
  );
}

// Wrapper to extract matchId from URL
function LiveMatchViewWrapper() {
  const matchId = window.location.pathname.split('/').pop();
  return <LiveMatchViewRouter matchId={parseInt(matchId)} />;
}

function LineupManagerWrapper() {
  const matchId = window.location.pathname.split('/').pop();
  return <LineupManager matchId={parseInt(matchId)} />;
}

export default App;
